import * as React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Link,
  Dialog,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import {
  BillingOverviewSelector,
  fetchBillingOverview,
} from "../../../redux/CorporateInvoice/CorporateBillingInfo";

const tableHeaders = ["Invoice ID", "Date", "Amount", "Status", "Actions"];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'invoice-generated':
      return '#f7d327';
    case 'paid':
      return '#52c173';
    case 'expired':
      return '#ef6762';
    default:
      return '#6C757D';
  }
};

const getStatusBackground = (status: string) => {
  switch (status) {
    case 'invoice-generated':
      return '#fffbe9';
    case 'paid':
      return '#ecf8f0';
    case 'expired':
      return '#feeeee';
    default:
      return '#F8F9FA';
  }
};

const statusLabels: Record<string, string> = {
  'invoice-generated': 'Pending',
  'paid': 'Paid',
  'expired': 'Expired',
};

const rowsPerPage = 10;

interface BillingTableCardProps {
  open: boolean;
  handleClose: () => void;
  code: string;
}

const BillingTableCard: React.FC<BillingTableCardProps> = ({ open, handleClose, code }) => {
  const dispatch = useDispatch();
  const { overview, loading } = BillingOverviewSelector();
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    if (open) {
      dispatch(fetchBillingOverview(code) as any);
    }
  }, [dispatch, open, code]);

  const invoices = React.useMemo(() => overview?.invoices || [], [overview]);
  const totalPages = Math.ceil(invoices.length / rowsPerPage);
  const paginatedInvoices = React.useMemo(
    () => invoices.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [invoices, page]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "20px" },
        }}
      >
        <DialogContent
        sx={{
          p: 0,
        }}
        >
          {loading ? (
            <CardContent>
            <Typography>Loading...</Typography>
            </CardContent>
          ) : !overview ? (
            <Card>
              <CardContent>
                <Typography>No billing data found</Typography>
              </CardContent>
            </Card>
          ) : (
            <Card
              sx={{
                borderRadius: "20px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                backgroundColor: "#FFFFFF",
                border: "1px solid #F0F0F0",
                p: 2,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#212529",
                      fontSize: "20px",
                      fontFamily: "InterBold",
                    }}
                  >
                    Billing
                  </Typography>
                  <IconButton size="small" sx={{ color: "#6C757D" }} onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>

                {/* Table */}
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "none",
                    borderRadius: 2,
                    border: "1px solid #E9ECEF",
                    backgroundColor: "#FFFFFF",
                    maxHeight: 350,
                    minHeight: 350,
                    overflow: "auto"
                  }}
                >
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#F8F9FA",
                          "& th": {
                            borderBottom: "2px solid #E9ECEF",
                          },
                        }}
                      >
                        {tableHeaders.map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              fontWeight: 600,
                              color: "#000",
                              fontSize: "14px",
                              py: 2.5,
                              px: 3,
                              fontFamily: "InterSemiBold",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedInvoices.length > 0 ? (
                        paginatedInvoices.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.stripe_invoice_id || row.invoice_id}</TableCell>
                            <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                            <TableCell>${row.price}</TableCell>
                            <TableCell>
                              <Chip
                                label={statusLabels[row.payment_status] || row.payment_status}
                                 sx={{
                                  backgroundColor: getStatusBackground(row.payment_status),
                                  color: getStatusColor(row.payment_status),
                                  fontWeight: 400,
                                  borderRadius: '20px',
                                  border: row.payment_status === 'Refunded' ? '1px solid #000' : `1px solid ${getStatusColor(row.payment_status)}`,
                                  fontSize: '14px',
                                  height: '28px',
                                  fontFamily: 'InterRegular',
                                  minWidth: '80px',
                                  '& .MuiChip-label': {
                                    px: 2,
                                    py: 0.5
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Link
                                href={row.payment_status === "expired" ? undefined : row.stripe_invoice_pdf_url}
                                onClick={(e) => {
                                  if (!row.stripe_invoice_pdf_url || row.payment_status === "expired") {
                                    e.preventDefault();
                                  }
                                }}
                                sx={{
                                  color: row.stripe_invoice_pdf_url && row.payment_status !== "expired" ? "#000" : "#aaa",
                                  textDecoration:
                                    row.stripe_invoice_pdf_url && row.payment_status !== "expired"
                                      ? "underline !important"
                                      : "none",
                                  pointerEvents:
                                    !row.stripe_invoice_pdf_url || row.payment_status === "expired" ? "none" : "auto",
                                  cursor:
                                    !row.stripe_invoice_pdf_url || row.payment_status === "expired" ? "default" : "pointer",
                                }}
                              >
                                Download Invoice
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={tableHeaders.length} sx={{ 
                            textAlign: "center", 
                            height: 250,
                            py: 5,
                            alignItems: "center",
                            justifyContent: "center",
                            }}>
                            <Typography>No invoice is available</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 4,
                  }}
                >
                  <Typography variant="body2">
                    {invoices.length > 0
                      ? `Showing ${(page - 1) * rowsPerPage + 1} - ${Math.min(
                          page * rowsPerPage,
                          invoices.length
                        )} of ${invoices.length}`
                      : ""}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    shape="rounded"
                    color="primary"
                    size="large"
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BillingTableCard